import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleType } from '@thive/fddm-types';

import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useUvtObjectValidation } from '@/modules/vehicles/validation/useUvtObjectValidation';
import { ValidAssetType, ValidFileExtension } from '@/modules/vehicles/types';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';
import { logger } from '@common/logging/logger';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import {
  PROJECT_FILES_ROOT_CONTAINER,
  KM_MDB_ASSET_FILE_CONTAINER,
  UVT_ASSET_FILE_CONTAINER,
} from '@/modules/api/constant';
import { VehiclesUtil } from '../vehiclesUtil';

type UploadAssetFileResult = {
  success: boolean;
};

export const useVehicleAssetFileUpload = () => {
  const { t } = useTranslation('interface');
  const { showSnackbar } = useSnackbarStore();
  const { isValidUvtJson } = useUvtObjectValidation();
  const { getVehicleTypes } = useFloorplanDesignStore();

  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const vehicleSvc = useMemo(() => VehicleServiceFactory.getInstance().createVehicleService(), []);
  const fileApi = useMemo(() => FileServiceConnector.getInstance(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const uploadAssetFileToFileStorage = useCallback(
    async (
      file: Blob,
      fileRef: string,
      extension: ValidFileExtension,
    ): Promise<UploadAssetFileResult> => {
      try {
        const basePath = buildRevisionFileStorageBasePath();
        if (!basePath) throw Error('basePath not defined.');

        await fileApi.addFile(
          `${basePath}/${fileRef}`,
          file,
          extension,
          PROJECT_FILES_ROOT_CONTAINER,
        );

        return { success: true };
      } catch (error) {
        logger.error('Error uploading file:', error);

        return { success: false };
      }
    },
    [fileApi],
  );

  const uploadAndSyncFileToFloorplan = useCallback(
    async (file: File, assetType: ValidAssetType, vehicleVariantId: string): Promise<void> => {
      if (!file) {
        showSnackbar(t('errors:files.no_file_selected'));
        return;
      }

      appSvc.showLoadContainer('TRANSPARENT');

      try {
        let fileType: ValidFileExtension = 'mdb';
        let assetContainer = KM_MDB_ASSET_FILE_CONTAINER;

        if (assetType === 'uvt') {
          fileType = 'json';
          assetContainer = UVT_ASSET_FILE_CONTAINER;
        }

        const newFileRef = VehiclesUtil.buildVehicleAssetFileRef(
          assetContainer,
          vehicleVariantId,
          fileType,
        );

        const fileContent = await file.text();
        const blob = new Blob([fileContent], { type: `application/${fileType}` });

        if (assetType === 'uvt' && !isValidUvtJson(fileContent)) {
          return;
        }

        const fileUploadResult = await uploadAssetFileToFileStorage(blob, newFileRef, fileType);

        if (!fileUploadResult.success) {
          showSnackbar(t('errors:files.upload_failed'));
          return;
        }

        const matchingAsset = getVehicleTypes().find(
          (vehicleType) => vehicleType.id === vehicleVariantId,
        );

        if (!matchingAsset) {
          logger.error('Could not match the asset for:', vehicleVariantId);
          return;
        }

        let nextVehicleType: VehicleType;

        if (assetType === 'uvt') {
          nextVehicleType = {
            ...matchingAsset,
            uvtFileRef: newFileRef,
            uvtFileRefOriginal: false,
          };
        } else {
          nextVehicleType = {
            ...matchingAsset,
            kmMdbFileRef: newFileRef,
            kmMdbFileRefOriginal: false,
          };
        }

        fddmSvc.updateVehicleType(nextVehicleType);

        if (assetType === 'uvt') {
          vehicleSvc.removeVehicleDetails([vehicleVariantId]);

          const updatedVehicleTypes = getVehicleTypes();

          await vehicleSvc.ensureVehicleDetailsAvailability(
            [vehicleVariantId],
            updatedVehicleTypes,
          );
        }
      } catch (error) {
        logger.error('Error occurred during file upload and sync:', error);
        showSnackbar(t('errors:files.upload_failed'));
      } finally {
        appSvc.hideLoadContainer();
      }
    },
    [
      appSvc,
      fddmSvc,
      getVehicleTypes,
      isValidUvtJson,
      showSnackbar,
      t,
      uploadAssetFileToFileStorage,
      vehicleSvc,
    ],
  );

  return {
    uploadAndSyncFileToFloorplan,
  };
};
